import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  ArrowBackRounded,
  DownloadRounded,
  GraphicEq,
  LocalMovies,
  PersonalVideo,
  RecordVoiceOver,
} from '@mui/icons-material';
import Banner from '@src/assets/images/educator-banner-mobile.webp';
import DeviceIllustration from '@src/assets/images/mobile-desktop.svg';
import VbeeAppLogo from '@src/assets/images/vbee-app-logo.png';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import { LANDING_PAGE_URL } from '@src/configs';
import ROUTES from '@src/constants/route';
import { COLOR } from '@src/styles/color';
import { StyledEducatorsMobileWeb } from './index.style';

const FEATURES = [
  { titleKey: 'educatorsFeatureLectureDubbingTitle', Icon: PersonalVideo },
  { titleKey: 'educatorsFeatureVideoDubbingTitle', Icon: LocalMovies },
  { titleKey: 'educatorsFeatureCreateAudioTitle', Icon: GraphicEq },
  { titleKey: 'voiceCloningLabel', Icon: RecordVoiceOver },
];

/**
 * Universal Link. The OS opens the app when it is installed, and the landing
 * page redirects to the store matching the device when it is not, so the web
 * never has to guess whether the app is there. It has to be a real anchor —
 * iOS ignores universal links opened from a tab a script created.
 */
const DOWNLOAD_APP_URL = `${LANDING_PAGE_URL}/apps/aivoice-tts`;

const BACK_BUTTON_STYLE = {
  height: '40px',
  padding: '8px 16px',
  gap: '8px',
  borderRadius: '8px',
  background: COLOR.white,
  border: `1px solid ${COLOR.indigo[16]}`,
  textTransform: 'none',
  '&:hover': {
    background: COLOR.white,
    border: `1px solid ${COLOR.indigo[16]}`,
  },
  // The design draws the arrow a shade lighter than its label.
  '& .MuiSvgIcon-root': { fontSize: '24px', color: COLOR.indigo[80] },
  '& .MuiTypography-root': { letterSpacing: 0 },
};

const DOWNLOAD_BUTTON_STYLE = {
  height: '32px',
  flexShrink: 0,
  padding: '4px 8px',
  gap: '4px',
  borderRadius: '8px',
  background: COLOR.indigo.default,
  textTransform: 'none',
  '&:hover': { background: COLOR.indigo.default },
  '& .MuiSvgIcon-root': { fontSize: '20px', color: COLOR.white },
};

const MobileWeb = () => {
  const { t } = useTranslation();
  const history = useHistory();

  return (
    <StyledEducatorsMobileWeb>
      {/* One artwork for every language: unlike the desktop banner, the
          headline in this design is English. */}
      <img
        className="educators-banner"
        src={Banner}
        alt={t('vbeeForEducators')}
      />

      <div className="educators-overview">
        <img
          className="educators-illustration"
          src={DeviceIllustration}
          alt=""
        />

        <div className="educators-details">
          <Typography
            variant="body-14-regular"
            color={COLOR.label2[64]}
            align="center"
            className="educators-description"
          >
            {t('educatorsMobileUnsupported')}
          </Typography>

          <div className="educators-features">
            {FEATURES.map(({ titleKey, Icon }) => (
              <div key={titleKey} className="educators-feature">
                <Icon />
                <Typography variant="body-12-medium" color={COLOR.indigo[80]}>
                  {t(titleKey)}
                </Typography>
              </div>
            ))}
          </div>

          <CommonButton
            variant="outlined"
            customStyle={BACK_BUTTON_STYLE}
            onClick={() => history.push(ROUTES.MOBILE_TTS)}
          >
            <ArrowBackRounded />
            <Typography variant="body-14-semibold" color={COLOR.indigo.default}>
              {t('educatorsBackToStudio')}
            </Typography>
          </CommonButton>
        </div>
      </div>

      <div className="educators-app-cta">
        <div className="educators-app-group">
          <div className="educators-app-icon">
            <img src={VbeeAppLogo} alt="" />
          </div>

          <div className="educators-app-info">
            <Typography
              variant="body-14-semibold"
              color={COLOR.darkGray}
              className="educators-app-name"
            >
              {t('vbeeAiVoice')}
            </Typography>
            <Typography
              variant="body-12-regular"
              color={COLOR.label2[64]}
              className="educators-app-subtitle"
            >
              {t('mobileAppV2')}
            </Typography>
          </div>
        </div>

        <CommonButton
          component="a"
          href={DOWNLOAD_APP_URL}
          customStyle={DOWNLOAD_BUTTON_STYLE}
        >
          <DownloadRounded />
          <Typography variant="body-14-medium" color={COLOR.white}>
            {t('downloadTheApp')}
          </Typography>
        </CommonButton>
      </div>
    </StyledEducatorsMobileWeb>
  );
};

export default MobileWeb;
